import React, { useState } from 'react';
import { SessionUser } from '../types';

interface Props {
  current: string;
  onNavigate: (page: string, params?: Record<string, string>) => void;
  user: (SessionUser & { bio?: string; email?: string }) | null;
}

const NAV_ITEMS = [
  { id: 'feed',          label: 'Feed',          icon: HomeIcon,         authOnly: false },
  { id: 'colors',        label: 'Colors',        icon: ColorIcon,        authOnly: false },
  { id: 'search',        label: 'Search',        icon: SearchIcon,       authOnly: false },
  { id: 'notifications', label: 'Notifications', icon: BellIcon,         authOnly: true  },
  { id: 'profile',       label: 'Profile',       icon: ProfileIcon,      authOnly: true  },
];

export function Sidebar({ current, onNavigate, user }: Props) {
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    setLoggingOut(true);
    await fetch('/api/v1/auth/logout', { method: 'POST' });
    window.location.href = '/';
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">mylodic</div>

      {NAV_ITEMS.map(item => {
        const Icon = item.icon;
        const active = current === item.id;
        if (item.authOnly && !user) return null;
        return (
          <button
            key={item.id}
            className={`sidebar-nav-item${active ? ' active' : ''}`}
            onClick={() => {
              const handle = user?.username && user.username !== 'null' ? user.username : null;
              if (item.id === 'profile' && handle) {
                onNavigate('user', { username: handle });
              } else {
                onNavigate(item.id);
              }
            }}
          >
            <Icon size={20} />
            {item.label}
          </button>
        );
      })}

      {user ? (
        <button className="sidebar-post-btn" onClick={() => onNavigate('post')}>
          + New Moment
        </button>
      ) : (
        <a href="/api/v1/auth/login" className="sidebar-post-btn" style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}>
          Sign in
        </a>
      )}

      <div className="sidebar-spacer" />

      {user ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            className="sidebar-nav-item"
            onClick={() => {
              const handle = user?.username && user.username !== 'null' ? user.username : null;
              handle ? onNavigate('user', { username: handle }) : onNavigate('profile');
            }}
            style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}
          >
            <Avatar url={user.avatar_url} name={user.display_name ?? user.username ?? '?'} size={32} />
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.display_name ?? user.username}</div>
              <div className="sidebar-user-handle">{user.username ? `@${user.username}` : user.email ?? ''}</div>
            </div>
          </button>

          <button
            onClick={handleLogout}
            disabled={loggingOut}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', fontSize: 13, transition: 'color 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
          >
            <LogoutIcon />
            {loggingOut ? 'Logging out...' : 'Log out'}
          </button>
        </div>
      ) : (
        <a
          href="/api/v1/auth/login"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}
        >
          <ProfileIcon size={16} />
          Sign in to post
        </a>
      )}
    </aside>
  );
}

function Avatar({ url, name, size }: { url: string | null; name: string; size: number }) {
  const [failed, setFailed] = React.useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, flexShrink: 0 }}>
      {(name[0] ?? '?').toUpperCase()}
    </div>
  );
}

function HomeIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/>
      <path d="M9 21V12h6v9"/>
    </svg>
  );
}

function ColorIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 3a9 9 0 010 18" fill="currentColor" opacity="0.15"/>
      <circle cx="8"  cy="10" r="1.5" fill="currentColor" stroke="none"/>
      <circle cx="16" cy="10" r="1.5" fill="currentColor" stroke="none" opacity="0.6"/>
      <circle cx="12" cy="15" r="1.5" fill="currentColor" stroke="none" opacity="0.35"/>
    </svg>
  );
}

function ProfileIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  );
}

function SearchIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function BellIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  );
}
